import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { TeButton, TeButtonMinimal, TeContainer } from '@topcon/tuicl-react';
import { RightDrawer, useTranslation } from '@topcon/topcon-utils';

import ErrorExplainer from '../../components/ErrorExplainer/ErrorExplainer';
import ErrorNotificationCard from '../../components/ErrorNotificationCard/ErrorNotificationCard';
import { getErrorContent } from '../../utils/errorContent';
import { parseProblemDetails } from '../../utils/problemDetails';
import type { ProblemDetails } from '../../utils/problemDetails.types';
import type { ErrorDrawerContextValue } from './error-drawer.types';

const ErrorDrawerContext = createContext<ErrorDrawerContextValue | undefined>(undefined);

// Keep the overlay above the app chrome; matches the app overlay stacking level.
const DRAWER_Z_INDEX = 1002;

const drawerOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  right: 0,
  height: '100vh',
  zIndex: DRAWER_Z_INDEX,
};

const cardOverlayStyle: React.CSSProperties = {
  position: 'fixed',
  top: 16,
  right: 16,
  zIndex: DRAWER_Z_INDEX,
};

interface ErrorDrawerProviderProps {
  readonly children: React.ReactNode;
}

/**
 * Provides a single global error drawer. Components call `showError(error)` to
 * surface an RFC 9457 problem response; non-problem errors are ignored so the
 * caller can keep its own fallback (e.g. a toast).
 * @param props - The wrapped application tree.
 * @returns The provider with the mounted drawer.
 */
export function ErrorDrawerProvider({ children }: ErrorDrawerProviderProps): React.JSX.Element {
  const { t } = useTranslation('errors');
  const [problem, setProblem] = useState<ProblemDetails | null>(null);
  const [expanded, setExpanded] = useState(false);

  const showError = useCallback((error: unknown): boolean => {
    const parsed = parseProblemDetails(error);
    if (parsed !== undefined) {
      setProblem(parsed);
      setExpanded(false);
      return true;
    }
    return false;
  }, []);

  const expand = useCallback((): void => setExpanded(true), []);

  const close = useCallback((): void => {
    setProblem(null);
    setExpanded(false);
  }, []);

  const value = useMemo<ErrorDrawerContextValue>(
    () => ({ problem, showError, close }),
    [problem, showError, close]
  );

  const content = problem !== null ? getErrorContent(t, problem.code) : undefined;
  const knowledgeBaseUrl = content?.knowledgeBaseUrl;

  return (
    <ErrorDrawerContext.Provider value={value}>
      {children}
      {createPortal(
        <>
          {problem !== null && content !== undefined && !expanded && (
            <div style={cardOverlayStyle}>
              <ErrorNotificationCard
                title={`${problem.code} - ${content.title}`}
                description={content.description}
                moreInfoLabel={t('moreInformation')}
                onMoreInfo={expand}
                onClose={close}
              />
            </div>
          )}
          <div style={drawerOverlayStyle}>
            <RightDrawer open={problem !== null && expanded}>
              {problem !== null && content !== undefined && (
                <TeContainer column fill grow>
                  <TeContainer justifyContent="flex-end" padding="s">
                    <TeButtonMinimal icon="close" variant="neutral" size="s" teclick={close} />
                  </TeContainer>
                  <TeContainer column grow>
                    <ErrorExplainer code={problem.code} content={content} />
                  </TeContainer>
                  {knowledgeBaseUrl !== undefined && (
                    <TeContainer padding="m">
                      <TeButton
                        text={t('openKnowledgeBase')}
                        variant="secondary"
                        grow
                        teclick={() =>
                          window.open(knowledgeBaseUrl, '_blank', 'noopener,noreferrer')
                        }
                      />
                    </TeContainer>
                  )}
                </TeContainer>
              )}
            </RightDrawer>
          </div>
        </>,
        document.body
      )}
    </ErrorDrawerContext.Provider>
  );
}

/**
 * Accesses the global error drawer.
 * @returns The error drawer context value.
 * @throws When used outside an `ErrorDrawerProvider`.
 */
export function useErrorDrawer(): ErrorDrawerContextValue {
  const context = useContext(ErrorDrawerContext);
  if (context === undefined) {
    throw new Error('useErrorDrawer must be used within an ErrorDrawerProvider');
  }
  return context;
}
